/**
 * Command Core - Pure logic for the Command prestige layer (M2)
 *
 * No browser/Alpine dependencies: consumed by the UI wrapper (command.js)
 * AND the headless simulator. Player-facing verb is promote/promotion -
 * "retire" never appears in copy.
 *
 * Key concepts:
 * - Command Marks: prestige currency from promoting characters (formula
 *   coefficients live in progressionScaling.commandMarks - balance SSOT).
 * - Legends: immutable snapshots of promoted characters (gallery).
 * - Tours: account-global counter, +1 per promotion (any faction).
 * - Region availability is DERIVED: faction starting pair + charter unlocks
 *   (+ everything, for grandfathered pre-Command saves).
 */

import * as config from '../constants.js';
import { gameState, defaultCommandState, ensureLifetimeCounters } from './gameState-core.js';
import { findItem } from './util.js';
import { setCraftingCostScaling, getCraftingCostMultiplier } from "./raidUtil.js";
import { getBlackoutPerk } from './blackout-core.js';

/**
 * Per-faction starting regions: home + best-second by native skill coverage
 * (tools/loot-equilibrium.mjs section 1; matches where the sticky player
 * model organically settles). Regions are intentionally asymmetrical.
 */
export const FACTION_STARTING_REGIONS = {
    dominion: ['cradle-expanse', 'shard-cities'],
    wilders: ['verdant-reclamation', 'sunken-bastions'],
    veil: ['hollow-core', 'sunken-bastions']
};

export function ensureCommandState() {
    if (!gameState.command) {
        gameState.command = defaultCommandState();
    }
    return gameState.command;
}

export function getUpgradeRank(upgradeId) {
    return gameState.command?.upgrades?.[upgradeId] || 0;
}

/**
 * Whether duplicate crafting of durable gear/artifacts is permitted. Off by
 * default (see progressionScaling.allowDuplicateCrafts): equip-once items are
 * capped at one craft per character - a duplicate earns no marks (distinct-only
 * formula) and has no other use, so it is a pure material sink and a footgun.
 */
export function duplicateCraftsAllowed() {
    return config.progressionScaling?.allowDuplicateCrafts === true;
}

/**
 * Whether the character already owns a given tech gear design (inventory OR
 * equipped - equipping can move the piece out of inventory). Used to enforce
 * the one-craft-per-design cap and to render the "✓ CRAFTED" card state.
 */
export function ownsTechGear(character, itemId) {
    if ((character?.inventory?.[itemId] || 0) > 0) return true;
    return Object.values(character?.equippedTechniqueGear || {})
        .some(g => g && (g.id === itemId || g === itemId));
}

/**
 * Whether the character has already crafted a given artifact design (crafted
 * artifacts live in character.artifacts, one instance per craft).
 */
export function ownsArtifact(character, artifactId) {
    return (character?.artifacts || []).some(a => a && a.id === artifactId);
}

/**
 * Effect value of an upgrade at its currently owned rank (0 ranks -> fallback).
 */
export function getUpgradeEffect(upgradeId, fallback = 0) {
    const rank = getUpgradeRank(upgradeId);
    if (rank <= 0) return fallback;
    const def = config.commandUpgrades?.[upgradeId];
    return def?.effectValues?.[rank - 1] ?? fallback;
}

/**
 * Distinct faction tech gear owned by a character (inventory + equipped).
 * Feeds the marks formula's strongest axis (full set = 15).
 */
export function countOwnedTechGear(character) {
    if (!character) return 0;
    const owned = new Set();
    Object.entries(character.inventory || {}).forEach(([itemId, qty]) => {
        if (qty > 0 && findItem('id', itemId)?.type === 'techGear') {
            owned.add(itemId);
        }
    });
    Object.values(character.equippedTechniqueGear || {}).forEach(gearId => {
        if (gearId && findItem('id', gearId)?.type === 'techGear') {
            owned.add(gearId);
        }
    });
    return owned.size;
}

/**
 * Command Marks a character is worth right now.
 * Returns { total, breakdown } - breakdown feeds the promote modal's table.
 */
export function calculateCommandMarks(character) {
    // Direct read, no fallback: an inline default here would silently compute a
    // DIFFERENT marks curve if constants.js were stale, and the old fallback had
    // itself drifted years out of date while reading like documentation.
    // configValidation.js proves this exists at boot.
    const c = config.progressionScaling.commandMarks;
    const techGear = countOwnedTechGear(character);
    const artifactsUnlocked = character?.lifetime?.artifactsUnlocked || 0;
    const artifacts = character?.lifetime?.artifactsCrafted || 0;
    const t3Wins = character?.lifetime?.t3Wins || 0;
    const level = character?.level || 1;

    // All banked at promotion. Artifacts split into the one-time-per-design
    // UNLOCK milestone (the account-level achievement, credited to whichever
    // character consumed the blueprint) and the repeatable per-CRAFT value.
    const breakdown = {
        techGear: { count: techGear, marks: techGear * c.perTechGear },
        artifactUnlocks: { count: artifactsUnlocked, marks: artifactsUnlocked * (c.perArtifactUnlock ?? 0) },
        artifacts: { count: artifacts, marks: artifacts * (c.perArtifactCraft ?? 0) },
        t3Wins: { count: t3Wins, marks: Math.floor(t3Wins / c.t3WinsDivisor) },
        level: { count: level, marks: level * c.perLevel }
    };
    const total = Object.values(breakdown).reduce((sum, b) => sum + b.marks, 0);
    return { total, breakdown };
}

/**
 * Whether an artifact design has been unlocked for the account (its blueprint
 * consumed via unlockArtifactCore). Unlocking is a one-time account milestone;
 * once unlocked, any character can craft the design from materials alone.
 */
export function isArtifactUnlocked(artifactId) {
    return (ensureCommandState().unlockedArtifacts || []).includes(artifactId);
}

/**
 * Consume one of the design's blueprints to unlock it for the account.
 * Crafting no longer needs a blueprint (removed from the recipe) - it needs
 * the design to be unlocked. The one-time unlock marks are credited to the
 * unlocking character's lifetime counter and bank at their promotion (like
 * every other marks source), so the promote dialog shows the full picture.
 * @returns {{success: boolean, reason?: string, marks?: number}}
 */
export function unlockArtifactCore(artifactId) {
    const command = ensureCommandState();
    if (!command.unlockedArtifacts) command.unlockedArtifacts = [];
    if (command.unlockedArtifacts.includes(artifactId)) {
        return { success: false, reason: 'already-unlocked' };
    }
    const blueprintId = `${artifactId}-blueprint`;
    const character = gameState.character;
    if (!character || (character.inventory?.[blueprintId] || 0) < 1) {
        return { success: false, reason: 'no-blueprint' };
    }
    character.inventory[blueprintId] -= 1;
    if (character.inventory[blueprintId] <= 0) delete character.inventory[blueprintId];
    command.unlockedArtifacts.push(artifactId);
    ensureLifetimeCounters(character);
    character.lifetime.artifactsUnlocked = (character.lifetime.artifactsUnlocked || 0) + 1;
    const marks = config.progressionScaling?.commandMarks?.perArtifactUnlock || 0;
    return { success: true, marks };
}

/**
 * One-way reveal: the Command tab appears when the active character's
 * estimated marks first reach the unlock threshold, then stays.
 */
export function isCommandRevealed() {
    const command = ensureCommandState();
    if (command.revealed) return true;
    const threshold = config.progressionScaling.commandMarks.unlockThreshold;
    if (gameState.character && calculateCommandMarks(gameState.character).total >= threshold) {
        command.revealed = true;
    }
    return command.revealed;
}

/**
 * Regions a character can raid: faction starting pair + charter unlocks.
 * Grandfathered pre-Command saves (and unknown factions) see everything.
 * Returns keys in config order for stable UI.
 */
export function availableRegionsForCharacter(character) {
    const allRegions = Object.keys(config.raidRegions);
    const command = ensureCommandState();
    if (command.grandfathered) return allRegions;
    const starts = FACTION_STARTING_REGIONS[character?.faction];
    if (!starts) return allRegions;
    const available = new Set([...starts, ...(command.charterRegions || [])]);
    return allRegions.filter(r => available.has(r));
}

/**
 * The promoted counterpart of the character's field role, via promotedRoles[]
 * in flavorText.js (index-matched to roles[] per skill). Falls back to a
 * name-hashed promoted role for the character's best skill (deterministic,
 * so the promote dialog preview always matches the enshrined Legend), then
 * to the title. Returns { role, quote } — quote is the command member's
 * role-specific welcome (promotedQuotes[], index-matched), null if unset.
 */
export function resolvePromotedFlavor(character) {
    const skills = character?.skills || {};
    const bestSkill = Object.keys(skills).sort((a, b) => (skills[b] || 0) - (skills[a] || 0))[0];
    const flavor = config.contractorSkillFlavors?.[bestSkill];
    const promoted = flavor?.promotedRoles;
    if (!promoted?.length) return { role: character?.title || null, quote: null };
    let idx = flavor.roles?.indexOf(character?.title) ?? -1;
    if (idx < 0 || !promoted[idx]) {
        // Deterministic fallback: hash the name so preview and Legend agree
        const name = character?.name || '';
        let h = 0;
        for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
        idx = Math.abs(h) % promoted.length;
    }
    return { role: promoted[idx], quote: flavor.promotedQuotes?.[idx] || null };
}

export function resolvePromotedRole(character) {
    return resolvePromotedFlavor(character).role;
}

/**
 * Can the active character be promoted right now?
 * Returns { ok, reason } - reasons map to UI copy in command.js.
 */
export function canPromoteCore() {
    const character = gameState.character;
    if (!character) return { ok: false, reason: 'no-active-character' };
    if (character.isMarketplace || character.marketplaceData) {
        return { ok: false, reason: 'listed-on-marketplace' };
    }
    // Any owned raid state blocks promotion (planning grid, active run, or an
    // unclaimed completed raid) - same rule character deletion applies
    if (gameState.currentRaid?.raidOwnerId === gameState.activeCharacterId &&
        (gameState.currentRaid.currentRaidGrid || gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted)) {
        return { ok: false, reason: 'raid-in-progress' };
    }
    return { ok: true };
}

/**
 * Where each item type goes when a character leaves the roster.
 *
 * One function because promotion and founding must agree: they are the same
 * event from the economy's point of view (a character stops existing and their
 * kit has to land somewhere), and two copies would drift the moment either is
 * touched.
 *
 *   blueprint, storyRelic — filed with Command at 100%, vault rank or not.
 *       Blueprints are the account-long artifact arc (a 3-5 day tour rarely
 *       finds even one). Story relics are Tier-4-only collectibles: destroying
 *       them at promotion would mean they can never be collected at all, since
 *       no tour outlives the layer.
 *
 *   material — SPLIT: progressionScaling.vaultCarryover to the successor,
 *              remainder consigned
 *       for Operations. See promoteCharacterCore.
 *
 *   relic, consumable — LIQUIDATED to credits. A relic is a credit note with an
 *       extra click; a consumable has a use, but Succession Doctrine promotes on
 *       its own schedule, so "spend it before you promote" is not something the
 *       player can reliably act on. Destroying either taxes forgetting rather
 *       than deciding.
 *
 *   techGear, artifact — dropped. These ARE the tour's achievement and are
 *       already paid for in marks; carrying them forward would let one character
 *       hand the next a finished set and collapse the Command loop.
 *
 * The caller decides what to do with the credits, because promotion levies them
 * and founding resets them outright.
 *
 * @returns {{credits:number, liquidated:number}} credits owed, and how many
 *   item stacks were sold to raise them.
 */
export function disburseInventory(inventory, { vaultShare = 0, vault = null, consignment = null, consignRate = null } = {}) {
    let credits = 0;
    let liquidated = 0;
    let consumed = 0;
    if (consignRate == null) consignRate = config.progressionScaling.consignmentRate;

    Object.entries(inventory || {}).forEach(([itemId, qty]) => {
        if (qty <= 0) return;
        const item = findItem('id', itemId);
        switch (item?.type) {
            case 'blueprint':
            case 'storyRelic':
                if (vault) vault[itemId] = (vault[itemId] || 0) + qty;
                break;
            case 'material': {
                const kept = Math.floor(qty * vaultShare);
                if (kept > 0 && vault) vault[itemId] = (vault[itemId] || 0) + kept;
                // What the successor does not inherit is NOT all convoy cargo.
                // Only `consignmentRate` of it reaches the Syndicate; the rest
                // is spent living — the settlement eats, repairs and rebuilds
                // out of the same stock. See progressionScaling.consignmentRate
                // for why the accounting had to stop being closed.
                const remainder = qty - kept;
                const shipped = Math.floor(remainder * consignRate);
                if (shipped > 0 && consignment) consignment[itemId] = (consignment[itemId] || 0) + shipped;
                consumed += remainder - shipped;
                break;
            }
            case 'relic':
            case 'consumable':
                if (item.credits > 0) {
                    credits += item.credits * qty;
                    liquidated += 1;
                }
                break;
            default:
                break;
        }
    });

    return { credits, liquidated, consumed };
}

/**
 * Promote the active character to faction command.
 *
 * Mutates gameState only (no DB/UI side effects - callers handle marketplace
 * withdrawal, contractor regeneration, and opening character creation):
 * - banks marks, enshrines a Legend snapshot, increments the global tour
 * - Guild Vault: carryover % of materials stored in command.vault for the
 *   NEXT created character (claimVaultForNewCharacter)
 * - removes the character, levies the promotion credit cut (credits are
 *   account-shared, so it's a percentage: keep keepFraction, min floor —
 *   progressionScaling.promotionCreditLevy)
 */
export function promoteCharacterCore() {
    const eligible = canPromoteCore();
    if (!eligible.ok) return { success: false, reason: eligible.reason };

    const character = gameState.character;
    const command = ensureCommandState();
    ensureLifetimeCounters(character);
    const marks = calculateCommandMarks(character);
    // Skeleton Crew's perk. Applied to the BANKED total rather than inside
    // calculateCommandMarks, because that function is also the promote modal's
    // preview and the succession doctrine's rate input — inflating it there
    // would make the peak policy chase a number that is not what it measures.
    const blackoutMarks = getBlackoutPerk('promotion-marks', 0);
    if (blackoutMarks > 0) marks.total = Math.round(marks.total * (1 + blackoutMarks));

    // Guild Vault: SPLIT the outgoing character's materials, destroy nothing.
    //
    // A fixed share keeps materials for the next recruit (command.vault, as
    // before) and the remainder — which used to evaporate with the character —
    // is consigned to `syndicate.consignment` as Operations cargo. The promoted
    // member packs up their kit; what the successor does not inherit is what the
    // convoys carry.
    //
    // The CONSIGNMENT, not the live dock (`syndicate.stores`). Founding is
    // repeatable, so this era's promotions are provisioning the NEXT Syndicate;
    // writing straight to the dock would top up the current one and dissolve
    // the per-era budget.
    //
    // So the rank is now a DIAL between two destinations rather than a
    // save-versus-destroy switch, and rank 0 consigns 100% rather than losing
    // it. Flagged for S7: that inverts the upgrade's purchase logic, since marks
    // spent here make convoys leaner.
    //
    // Everything that is NOT material is routed by disburseInventory() — see
    // there for why blueprints and story relics are filed whole, and why relics
    // and consumables are sold rather than dropped.
    const vaultShare = config.progressionScaling.vaultCarryover;
    const consignment = gameState.syndicate ? (gameState.syndicate.consignment ||= {}) : null;
    const disbursed = disburseInventory(character.inventory, {
        vaultShare, vault: command.vault, consignment,
    });

    // Credited BEFORE the levy, deliberately. Relics and consumables are wealth,
    // and the levy exists because "credits are account-shared, so a flat reset
    // would be trivially dodged by holding wealth elsewhere". Crediting after
    // the levy would make stockpiling purchasable consumables exactly that dodge
    // — buy at full price before promoting, cash out untaxed after.
    if (disbursed.credits > 0) {
        gameState.player.credits = (gameState.player.credits || 0) + disbursed.credits;
    }

    const legend = {
        name: character.name,
        avatar: character.avatar, // legacy sprite class, kept as fallback
        avatarSeed: character.avatarSeed, // procedural portrait seed, so the Legend matches the character
        faction: character.faction,
        level: character.level || 1,
        tour: command.tour,
        techGear: marks.breakdown.techGear.count,
        artifactsUnlocked: marks.breakdown.artifactUnlocks.count,
        artifactsCrafted: marks.breakdown.artifacts.count,
        t3Wins: marks.breakdown.t3Wins.count,
        marksEarned: marks.total,
        promotedRole: resolvePromotedRole(character),
        promotedAt: Date.now()
    };

    command.marks += marks.total;
    // Writ input for the Syndicate layer (M3). Tracked separately from
    // command.marks because that is a spendable BALANCE - it drops every time
    // the player buys an upgrade, and Writs must price the era that was run,
    // not the change left in the player's pocket at the end of it. Every marks
    // source funnels through promotion, so this line is the whole accumulator.
    if (gameState.syndicate) gameState.syndicate.eraMarks += marks.total;
    command.legends.push(legend);
    command.tour += 1;
    // A new tour starts with a fresh rate curve — carrying the old peak forward
    // would have the doctrine judging the successor against their predecessor's
    // best run and promoting them almost immediately.
    if (gameState.syndicate?.succession) {
        gameState.syndicate.succession.tourRaids = 0;
        gameState.syndicate.succession.peakRate = 0;
        gameState.syndicate.succession.peakAtRaid = 0;
    }

    gameState.characters = gameState.characters.filter(c => c.id !== character.id);
    gameState.character = null;
    gameState.activeCharacterId = null;
    const levy = config.progressionScaling.promotionCreditLevy;
    gameState.player.credits = Math.max(levy.floor, Math.floor((gameState.player.credits || 0) * levy.keepFraction));

    // Re-derive from the new tour rather than compounding module state (see
    // applyCraftingDiscountForTour).
    applyCraftingDiscountForTour();

    return { success: true, marks, legend, disbursed };
}

/**
 * Crafting global multiplier for a given tour: base × factor^(tour − 1).
 * Pure, so the simulator and tests can assert the curve directly.
 */
export function craftingMultiplierForTour(tour) {
    const cfg = config.progressionScaling.craftingCostScaling;
    const factor = config.progressionScaling.craftingDiscountPerTour;
    const perTour = cfg.globalMultiplier * Math.pow(factor, Math.max(0, (tour || 1) - 1));
    // Cold Drop's perk. Multiplied INTO the curve rather than clamped under it:
    // the spec calls it a "floor" discount, and the per-tour curve is already a
    // decaying multiplier — a separate floor would fight it, and the deeper a
    // player goes the more a subtraction would distort. Same shape, one factor.
    return perTour * (1 - getBlackoutPerk('crafting-floor', 0));
}

/**
 * Push the tour-derived crafting multiplier into raidUtil's scaling state.
 *
 * Call at boot (after the config-seeded scaling is applied) and at promotion.
 * DERIVED, not compounded: the previous version multiplied the live module
 * state by 0.9 per promotion, but main.js re-seeds that state from config on
 * every boot, so the discount silently died on reload. Deriving from
 * `command.tour` is reload-safe and makes L3's founding reset free — tour
 * returns to 1, so the multiplier returns to base with nothing to persist.
 */
export function applyCraftingDiscountForTour() {
    const tour = ensureCommandState().tour;
    setCraftingCostScaling({
        globalMultiplier: craftingMultiplierForTour(tour),
        rarityMultipliers: config.progressionScaling.craftingCostScaling.rarityMultipliers
    });
    return getCraftingCostMultiplier();
}

/**
 * XP multiplier from the Legend Bonus upgrade: +effectValue per enshrined
 * Legend (1.0 when the upgrade isn't owned). Applied in gainExperienceCore.
 */
export function commandXpMultiplier() {
    const perLegend = getUpgradeEffect('legend-bonus', 0);
    if (perLegend <= 0) return 1;
    return 1 + perLegend * (gameState.command?.legends?.length || 0);
}

/**
 * Drain the Guild Vault into a newly created character's inventory.
 * Called from character creation.
 */
export function claimVaultForNewCharacter(character) {
    const command = ensureCommandState();
    const granted = {};
    Object.entries(command.vault || {}).forEach(([itemId, qty]) => {
        if (qty > 0) {
            character.inventory[itemId] = (character.inventory[itemId] || 0) + qty;
            granted[itemId] = qty;
        }
    });
    command.vault = {};
    return granted;
}

/**
 * Purchase the next rank of an upgrade with Command Marks.
 * Regional Charter requires a `choice` (region key to unlock).
 */
/**
 * Prerequisite upgrade ids a definition declares but the player does not own.
 *
 * `requires` accepts a string or an array so a single-prerequisite upgrade
 * doesn't have to write `['x']`. Returns [] when there is nothing to check,
 * which is every upgrade except the automation capstone today.
 */
/**
 * Marks price of an upgrade's next rank.
 *
 * ONE function so the roster card, the affordability check and the actual
 * deduction cannot disagree.
 *
 * ⚠ NO SYNDICATE DISCOUNT ANY MORE. `institutional-memory` used to cut this by
 * 20/35/50%, and S14 measured that it bought nothing: forcing the discount to
 * 90% across a batch of 8 runs moved the era-pacing targets not at all. Upgrade
 * cost does not set era length — the Legend requirement and tour length do —
 * so the charter was repointed at the Legend gate and this became a plain
 * lookup. See `foundingLegendRequirement` and docs/layers/3-syndicate/implementation.md § 2026-08-03 "Era pacing".
 *
 * @returns {number|null} cost, or null when the upgrade is maxed/unknown
 */
export function commandUpgradeCost(upgradeId, rank = getUpgradeRank(upgradeId)) {
    const def = config.commandUpgrades?.[upgradeId];
    if (!def || rank >= def.costs.length) return null;
    return def.costs[rank];
}

export function requiredUpgradesMissing(def) {
    if (!def?.requires) return [];
    const required = Array.isArray(def.requires) ? def.requires : [def.requires];
    return required.filter(id => getUpgradeRank(id) <= 0);
}

export function purchaseUpgradeCore(upgradeId, choice = null) {
    const def = config.commandUpgrades?.[upgradeId];
    if (!def) return { success: false, reason: 'unknown-upgrade' };

    const command = ensureCommandState();
    const rank = getUpgradeRank(upgradeId);
    if (rank >= def.costs.length) return { success: false, reason: 'max-rank' };

    // Prerequisite upgrades (auto-raid-protocol needs auto-scout + foremans-
    // ledger). Checked before the cost so a player is told what is missing
    // rather than being told they are short of marks they would waste anyway.
    const missing = requiredUpgradesMissing(def);
    if (missing.length > 0) {
        return { success: false, reason: 'missing-requirement', requires: missing };
    }

    const cost = commandUpgradeCost(upgradeId, rank);
    if (command.marks < cost) return { success: false, reason: 'insufficient-marks' };

    if (def.choice === 'region') {
        if (!choice || !config.raidRegions[choice]) {
            return { success: false, reason: 'invalid-region' };
        }
        if (command.charterRegions.includes(choice)) {
            return { success: false, reason: 'region-already-chartered' };
        }
    }

    command.marks -= cost;
    command.upgrades[upgradeId] = rank + 1;
    if (def.choice === 'region') {
        command.charterRegions.push(choice);
    }
    return { success: true, rank: rank + 1, cost };
}
